import { useState, useCallback } from 'react'
import Styles from './ChatAssistant.module.scss'
import { ChatMessages } from 'components/ChatAssistant/Messages/ChatMessages'
import { ChatInput } from 'components/ChatAssistant/ChatInput/ChatInput'
import { ChatAssistantProps } from './Types'
import { useCreateThread } from 'hooks/queries/chat/useCreateThread'
import { useCreateMessage } from 'hooks/queries/chat/useCreateMessage'
import { ChatMessageContent } from 'api/clients/ChatClient'
import { collectResults } from 'utils/query'
import { useInfiniteMessages } from 'hooks/queries/chat/useInfiniteMessages'
import { AIJobStatus } from 'types'
import { SubmitPayload } from './ChatInput/Types'
import { useAbortMessage } from 'hooks/queries/chat/useAbortMessage'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const ChatAssistant = ({ threadId, fileId, setSelectedThreadId, inputPlaceholder, context }: ChatAssistantProps) => {
  const isChatV2 = useFlipperFlag('chat_v2')
  const [value, setValue] = useState('')
  const { mutate: createThread, isLoading: isCreatingThread } = useCreateThread()
  const { mutate: createMessage, isLoading: isCreatingMessage } = useCreateMessage()
  const isLoading = isCreatingThread || isCreatingMessage
  const messagesQuery = useInfiniteMessages({ threadId, items: 5 })
  const messages = collectResults(messagesQuery, (data) => data.messages)

  const processingMessage = messages.find(
    (message) => message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status),
  )
  const isProcessing = !!processingMessage

  const { mutate: abortMessage } = useAbortMessage()

  const handleAbort = useCallback(() => {
    if (processingMessage) {
      abortMessage({ threadId: processingMessage.thread_id, id: processingMessage.id })
    }
  }, [abortMessage, processingMessage])

  const hasDesignContext = isChatV2 && !!context && 'design_id' in context

  const handleSubmit = ({ value: text, attachedImage }: SubmitPayload) => {
    const content: ChatMessageContent[] = []

    if (attachedImage) {
      content.push({
        type: 'image',
        image_url: attachedImage.url,
        media_file_key: attachedImage.mediaFileKey,
      })
    }

    content.push({ type: 'text', text })

    if (context) {
      content.push({ type: 'context', context })
    }

    if (!threadId) {
      createThread(
        { content, fileId },
        {
          onSuccess: (data) => {
            setValue('')
            setSelectedThreadId(data.thread.id)
          },
        },
      )
    } else {
      createMessage(
        { threadId, content },
        {
          onSuccess: () => {
            setValue('')
          },
        },
      )
    }
  }

  return (
    <div className={Styles.root}>
      <ChatMessages threadId={threadId} setValue={setValue} />
      <div className={Styles.footerContainer}>
        <ChatInput
          value={value}
          onChange={setValue}
          placeholder={inputPlaceholder}
          onSubmit={handleSubmit}
          isDisabled={isLoading || isProcessing}
          threadId={threadId}
          fileId={fileId}
          hasDesignContext={hasDesignContext}
          isProcessing={isProcessing}
          onAbort={handleAbort}
        />
      </div>
    </div>
  )
}

export { ChatAssistant }
